/**
 * InstallAppBanner — הצעה להתקין את האפליקציה למסך הבית
 *
 * A quiet, dismissible bar shown to mobile users who haven't installed yet.
 * Deliberately restrained: it waits until the cookie banner is out of the way,
 * never appears inside the installed app or on the guide page itself, and a
 * dismissal is remembered for 30 days (a "no" that comes back tomorrow is just
 * an ad). On Android it installs in one tap; elsewhere it links to /install.
 */

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { X, Download } from 'lucide-react'
import usePwaInstall from '../../hooks/usePwaInstall'
import { getCookieConsent } from './CookieConsentBanner'

const STORAGE_KEY = 'binat-install-banner-dismissed'
const SNOOZE_DAYS = 30
const APPEAR_DELAY_MS = 4000

function isSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const dismissedAt = Number(raw)
    if (!Number.isFinite(dismissedAt)) return false
    return Date.now() - dismissedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}

export default function InstallAppBanner() {
  const { isStandalone, canPrompt, promptInstall, platform } = usePwaInstall()
  const location = useLocation()
  const [visible, setVisible] = useState(false)

  const eligible =
    !isStandalone &&
    platform !== 'desktop' &&
    location.pathname !== '/install' &&
    // Don't stack two bars on top of each other — cookie consent goes first.
    getCookieConsent() !== null &&
    !isSnoozed()

  useEffect(() => {
    if (!eligible) {
      setVisible(false)
      return
    }
    // Let the user actually look at the page before offering anything.
    const timer = setTimeout(() => setVisible(true), APPEAR_DELAY_MS)
    return () => clearTimeout(timer)
  }, [eligible])

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()))
    } catch {
      /* storage unavailable — the banner just won't be remembered */
    }
    setVisible(false)
  }

  const install = async () => {
    const accepted = await promptInstall()
    if (accepted) setVisible(false)
  }

  if (!visible) return null

  return (
    <section
      aria-label="התקנת האפליקציה למסך הבית"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 p-3">
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 flex-shrink-0 rounded-xl" />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">התקינו את האפליקציה</p>
          <p className="truncate text-xs text-gray-600">גישה מהירה מהמסך הבית, בלי חנות אפליקציות</p>
        </div>

        {canPrompt ? (
          <button
            type="button"
            onClick={install}
            className="flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Download size={16} aria-hidden="true" />
            התקנה
          </button>
        ) : (
          <Link
            to="/install"
            onClick={() => setVisible(false)}
            className="flex min-h-[44px] flex-shrink-0 items-center rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
          >
            איך מתקינים?
          </Link>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label="סגירת ההצעה להתקנת האפליקציה"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>
    </section>
  )
}
